// Formatters specific to the Investor Profile modal. The card formatters
// (compact currency, property/purchase counts, ownership) already live in
// data/investors and are reused as-is — this file only adds the ones the
// profile's full metric grid needs that the card doesn't: full (non-compact)
// currency for "Total Equity" / "Est. Portfolio Value", and a plain count for
// rows like "Cash Purchases" / "Financed".
const EMPTY = '—';

const isMissing = (value) => value === null || value === undefined || Number.isNaN(Number(value));

// "$4,832,884" — the metric grid's second row (node 278:20073) never
// abbreviates, unlike the search card's "$248K".
export const formatCurrency = (value) => {
  if (isMissing(value)) return EMPTY;

  return `$${Math.round(Number(value)).toLocaleString('en-US')}`;
};

// "15 (100%)" — a count paired with its share of a total, e.g. cash purchases
// out of all purchases. Renders the count alone if the total is missing/zero.
export const formatCountWithShare = (count, total) => {
  if (isMissing(count)) return EMPTY;

  const n = Number(count);
  if (isMissing(total) || Number(total) <= 0) return n.toLocaleString('en-US');

  const pct = Math.round((n / Number(total)) * 100);
  return `${n.toLocaleString('en-US')} (${pct}%)`;
};

// Contact source indicator — "Skip Traced Aug 12, 2026" / "User Added Aug 12,
// 2026" (S5 build-spec: a per-number "User Added [date] / Skip Traced [date]"
// indicator).
export const formatContactSourceDate = (isoDate) => {
  if (!isoDate) return '';

  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

// A mailing address arrives as a structured record — { street, city, state,
// zip, zipPlus4, county, fullAddress, addressValidity, hash, propertyCount }
// — not a plain string, so it can never be rendered directly as a JSX child.
// `fullAddress` already carries the vendor's own formatted string when
// present; otherwise this assembles one from the parts. Also accepts a plain
// string as-is, in case a caller already has one.
export const formatMailingAddress = (address) => {
  if (!address) return '';
  if (typeof address === 'string') return address;
  if (address.fullAddress) return address.fullAddress;

  const zip = [address.zip, address.zipPlus4].filter(Boolean).join('-');
  const tail = [address.city, [address.state, zip].filter(Boolean).join(' ')]
    .filter(Boolean)
    .join(', ');

  return [address.street, tail].filter(Boolean).join(', ');
};
